import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { localizedPath } from '../utils/routes';

export default function EmailVerification() {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const { user, updateUser, logout, API } = useAuth();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { lang } = useParams();
  const currentLang = lang || language;
  const lp = (routeName) => localizedPath(routeName, currentLang);
  const tv = t.emailVerify;

  // Start 60s cooldown on mount (code was auto-sent at registration)
  useEffect(() => {
    setResendCooldown(60);
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // If already verified, redirect
  useEffect(() => {
    if (user?.email_verified) {
      navigate(lp('dashboard'), { replace: true });
    }
  }, [user, navigate]);

  const handleVerify = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setLoading(true);
    try {
      await axios.post(`${API}/email/verify/confirm`, { code: code.trim() });
      setSuccess(tv.success);
      updateUser({ email_verified: true });
      setTimeout(() => navigate(lp('dashboard'), { replace: true }), 1000);
    } catch (err) {
      setError(err.response?.data?.error || tv.verifyError);
    } finally { setLoading(false); }
  };

  const handleResend = async () => {
    setError(''); setSuccess('');
    try {
      await axios.post(`${API}/email/verify/send`);
      setResendCooldown(60);
    } catch (err) {
      setError(err.response?.data?.error || tv.sendError);
    }
  };

  const handleSignOut = () => {
    logout();
    navigate(lp('login'));
  };

  return (
    <div style={{ background: '#0C1220', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
      <div style={{ width: '100%', maxWidth: '440px' }}>
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '2rem', color: '#D42B2B', marginBottom: '0.3rem' }}>CJPA</div>
          <div style={{ fontSize: '0.58rem', letterSpacing: '0.1em', color: '#64748B', textTransform: 'uppercase' }}>Mécanique Auto</div>
          <h1 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '2.2rem', color: '#E8ECF0', fontWeight: 300, marginTop: '2rem' }}>{tv.title}</h1>
          <p style={{ color: '#94A3B8', fontSize: '0.78rem', marginTop: '0.5rem' }}>
            {tv.subtitle} <strong style={{ color: '#E8ECF0' }}>{user?.email}</strong>
          </p>
        </div>

        <div style={{ background: '#111827', border: '1px solid rgba(148,163,184,0.15)', padding: 'clamp(1.5rem, 3vw, 2.5rem)' }}>
          {error && <div style={{ background: 'rgba(180,50,50,0.1)', border: '1px solid rgba(180,50,50,0.3)', color: '#e08080', padding: '0.8rem 1rem', fontSize: '0.78rem', marginBottom: '1.5rem' }}>{error}</div>}
          {success && <div style={{ background: 'rgba(50,180,50,0.1)', border: '1px solid rgba(50,180,50,0.3)', color: '#80e080', padding: '0.8rem 1rem', fontSize: '0.78rem', marginBottom: '1.5rem' }}>{success}</div>}

          <form onSubmit={handleVerify}>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.6rem', letterSpacing: '0.1em', color: '#64748B', textTransform: 'uppercase', marginBottom: '0.6rem', fontFamily: "'Barlow', sans-serif" }}>{tv.enterCode}</label>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                style={{
                  width: '100%', background: '#1a2332', border: '1px solid rgba(148,163,184,0.2)',
                  color: '#E8ECF0', padding: '1rem', fontSize: '1.8rem',
                  fontFamily: "'Courier New', monospace", outline: 'none',
                  textAlign: 'center', letterSpacing: '0.5em',
                  transition: 'border-color 0.3s', boxSizing: 'border-box',
                }}
                onFocus={e => e.target.style.borderColor = '#D42B2B'}
                onBlur={e => e.target.style.borderColor = 'rgba(148,163,184,0.2)'}
              />
            </div>

            <button type="submit" disabled={loading || code.length !== 6} style={{
              width: '100%', background: '#D42B2B', color: '#0C1220', border: 'none',
              padding: '1rem', fontFamily: "'Barlow', sans-serif", fontSize: '0.65rem',
              letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 600,
              cursor: (loading || code.length !== 6) ? 'not-allowed' : 'pointer',
              opacity: (loading || code.length !== 6) ? 0.7 : 1, marginTop: '0.5rem',
            }}>{loading ? tv.verifying : tv.verify}</button>
          </form>

          <div style={{ textAlign: 'center', marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid rgba(148,163,184,0.1)' }}>
            {resendCooldown > 0 ? (
              <span style={{ color: '#64748B', fontSize: '0.78rem' }}>{tv.cooldown.replace('{seconds}', resendCooldown)}</span>
            ) : (
              <button onClick={handleResend} style={{
                background: 'none', border: 'none', color: '#D42B2B', fontSize: '0.78rem',
                cursor: 'pointer', fontFamily: "'Barlow', sans-serif", textDecoration: 'none',
              }}>{tv.resend}</button>
            )}
          </div>

          <div style={{ background: 'rgba(212,43,43,0.08)', border: '1px solid rgba(212,43,43,0.2)', padding: '0.8rem 1rem', marginTop: '1.2rem', textAlign: 'center' }}>
            <span style={{ color: '#94A3B8', fontSize: '0.75rem' }}>{tv.checkSpam}</span>
          </div>

          <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
            <button onClick={handleSignOut} style={{
              background: 'none', border: 'none', color: '#94A3B8', fontSize: '0.75rem',
              cursor: 'pointer', fontFamily: "'Barlow', sans-serif", textDecoration: 'underline',
            }}>{tv.signOut}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
